/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo, useRef } from 'react';

// porp-types is a library for typechecking of props
import PropTypes from 'prop-types';

// react-chartjs-2 components
import { Line } from 'react-chartjs-2';

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

// DefaultLineChart configurations
import configs from 'examples/Charts/LineCharts/DefaultLineChart/configs';

// Material Dashboard 2 PRO React base styles
import colors from 'assets/theme/base/colors';

function getOrCreateTooltip(chart) {
  let tooltipEl = chart.canvas.parentNode.querySelector('div.chartjs-html-tooltip');

  if (!tooltipEl) {
    tooltipEl = document.createElement('div');
    tooltipEl.className = 'chartjs-html-tooltip';

    Object.assign(tooltipEl.style, {
      background: 'rgba(30,30,30,0.92)',
      borderRadius: '8px',
      color: '#fff',
      opacity: 0,
      pointerEvents: 'none',
      position: 'absolute',
      transition: 'opacity 0.2s',
      zIndex: 9999,
      fontSize: '12px',
      padding: '8px 12px',
      minWidth: '140px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
    });

    const table = document.createElement('table');
    table.style.margin = '0';
    table.style.borderCollapse = 'collapse';
    tooltipEl.appendChild(table);
    chart.canvas.parentNode.appendChild(tooltipEl);
  }
  return tooltipEl;
}

function externalTooltipHandler(context) {
  const { chart, tooltip } = context;
  const tooltipEl = getOrCreateTooltip(chart);

  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = 0;
    return;
  }

  if (tooltip.body) {
    const titleLines = tooltip.title || [];
    const bodyLines = tooltip.body.map((b) => b.lines);

    const table = tooltipEl.querySelector('table');
    table.innerHTML = '';

    if (titleLines.length) {
      const thead = document.createElement('thead');
      const tr = document.createElement('tr');
      const th = document.createElement('th');
      th.colSpan = 2;
      th.style.paddingBottom = '6px';
      th.style.fontWeight = 'bold';
      th.style.fontSize = '13px';
      th.style.textAlign = 'left';
      th.style.whiteSpace = 'nowrap';
      th.appendChild(document.createTextNode(titleLines[0]));
      tr.appendChild(th);
      thead.appendChild(tr);
      table.appendChild(thead);
    }

    const tbody = document.createElement('tbody');
    bodyLines.forEach((body, i) => {
      const colors_item = tooltip.labelColors[i];
      const tr = document.createElement('tr');
      tr.style.borderTop = '0';

      const tdDot = document.createElement('td');
      tdDot.style.paddingRight = '6px';
      tdDot.style.paddingTop = '2px';
      const dot = document.createElement('span');

      Object.assign(dot.style, {
        display: 'inline-block',
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        background: colors_item.borderColor,
        marginRight: '4px',
        flexShrink: '0',
      });

      tdDot.appendChild(dot);

      const tdText = document.createElement('td');
      tdText.style.whiteSpace = 'nowrap';
      tdText.style.paddingTop = '2px';
      tdText.appendChild(document.createTextNode(body));

      tr.appendChild(tdDot);
      tr.appendChild(tdText);
      tbody.appendChild(tr);
    });
    table.appendChild(tbody);
  }

  const { offsetLeft: posX, offsetTop: posY } = chart.canvas;
  const canvasWidth = chart.canvas.offsetWidth;
  const canvasHeight = chart.canvas.offsetHeight;

  tooltipEl.style.opacity = 1;

  const tooltipWidth = tooltipEl.offsetWidth || 160;
  const tooltipHeight = tooltipEl.offsetHeight || 200;
  let left = posX + tooltip.caretX + 12;
  let top = posY + tooltip.caretY - tooltipHeight / 2;

  if (left + tooltipWidth > posX + canvasWidth) {
    left = posX + tooltip.caretX - tooltipWidth - 12;
  }
  if (top < posY) top = posY;
  if (top + tooltipHeight > posY + canvasHeight) {
    top = posY + canvasHeight - tooltipHeight;
  }

  tooltipEl.style.left = `${left}px`;
  tooltipEl.style.top = `${top}px`;
}

function DefaultLineChart({ icon, title, description, height, chart }) {
  const chartDatasets = chart.datasets
    ? chart.datasets.map((dataset) => ({
        ...dataset,
        tension: 0,
        pointRadius: 3,
        borderWidth: 4,
        backgroundColor: 'transparent',
        fill: true,
        pointBackgroundColor: colors[dataset.color]
          ? colors[dataset.color || 'dark'].main
          : colors.dark.main,
        borderColor: colors[dataset.color]
          ? colors[dataset.color || 'dark'].main
          : colors.dark.main,
        maxBarThickness: 6,
      }))
    : [];

  const { data, options } = configs(chart.labels || [], chartDatasets, externalTooltipHandler);

  const renderChart = (
    <MDBox py={2} pr={2} pl={icon.component ? 1 : 2}>
      {title || description ? (
        <MDBox display='flex' px={description ? 1 : 0} pt={description ? 1 : 0}>
          {icon.component && (
            <MDBox
              width='4rem'
              height='4rem'
              bgColor={icon.color || 'info'}
              variant='gradient'
              coloredShadow={icon.color || 'info'}
              borderRadius='xl'
              display='flex'
              justifyContent='center'
              alignItems='center'
              color='white'
              mt={-5}
              mr={2}
            >
              <Icon fontSize='medium'>{icon.component}</Icon>
            </MDBox>
          )}
          <MDBox mt={icon.component ? -2 : 0}>
            {title && <MDTypography variant='h6'>{title}</MDTypography>}
            <MDBox mb={2}>
              <MDTypography component='div' variant='button' color='text'>
                {description}
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      ) : null}
      {useMemo(
        () => (
          <MDBox height={height} sx={{ position: 'relative', overflow: 'visible' }}>
            <Line data={data} options={options} />
          </MDBox>
        ),
        [chart, height]
      )}
    </MDBox>
  );

  return title || description ? <Card>{renderChart}</Card> : renderChart;
}

// Setting default values for the props of DefaultLineChart
DefaultLineChart.defaultProps = {
  icon: { color: 'info', component: '' },
  title: '',
  description: '',
  height: '19.125rem',
};

// Typechecking props for the DefaultLineChart
DefaultLineChart.propTypes = {
  icon: PropTypes.shape({
    color: PropTypes.oneOf([
      'primary',
      'secondary',
      'info',
      'success',
      'warning',
      'error',
      'light',
      'dark',
    ]),
    component: PropTypes.node,
  }),
  title: PropTypes.string,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  chart: PropTypes.objectOf(PropTypes.array).isRequired,
};

export default DefaultLineChart;
